import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MARKET_SEASONS, EVENTS_POOL, COMPANIES_V2 } from './simulatorData';

/* ─────────────────────────────────────────────
   UTILITY COMPONENTS (preserved)
───────────────────────────────────────────── */
const TerminalText = ({ text, delay = 15, onComplete, className }) => {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    setDisplayed('');
    let i = 0;
    const timer = setInterval(() => {
      setDisplayed(text.slice(0, i));
      i++;
      if (i > text.length) { clearInterval(timer); if (onComplete) onComplete(); }
    }, delay);
    return () => clearInterval(timer);
  }, [text, delay, onComplete]);
  return <span className={className}>{displayed}</span>;
};

const DecryptText = ({ text }) => {
  const [scrambled, setScrambled] = useState(true);
  useEffect(() => { setScrambled(true); const t = setTimeout(() => setScrambled(false), 400); return () => clearTimeout(t); }, [text]);
  const chars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  const randomStr = text.split('').map(c => c === ' ' ? ' ' : chars[Math.floor(Math.random() * chars.length)]).join('');
  return <span>{scrambled ? randomStr : text}</span>;
};

/* ─────────────────────────────────────────────
   CANDLESTICK CHART
───────────────────────────────────────────── */
const CandlestickChart = ({ company, currentPrice }) => {
  const candles = useMemo(() => {
    const seed = company.id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    let price = currentPrice * 0.82;
    const trend = company.tabs.technicals.includes('Bullish') || company.tabs.technicals.includes('Ascending') ? 1.003 : company.tabs.technicals.includes('Downtrend') || company.tabs.technicals.includes('Bear') ? 0.997 : 1.001;
    const vol = company.tabs.technicals.includes('volatil') || company.tabs.technicals.includes('volatile') ? 0.045 : 0.022;
    const result = [];
    let rng = seed;
    const rand = () => { rng = (rng * 1664525 + 1013904223) & 0xffffffff; return (rng >>> 0) / 0xffffffff; };
    for (let i = 0; i < 40; i++) {
      const open = price;
      const move = (rand() - 0.48) * vol;
      const close = open * (trend + move);
      const high = Math.max(open, close) * (1 + rand() * vol * 0.5);
      const low = Math.min(open, close) * (1 - rand() * vol * 0.5);
      result.push({ open, close, high, low, vol: rand() * 0.8 + 0.2 });
      price = close;
    }
    return result;
  }, [company.id, currentPrice]);

  const W = 680, H = 280;
  const prices = candles.flatMap(c => [c.high, c.low]);
  const minP = Math.min(...prices), maxP = Math.max(...prices);
  const range = maxP - minP || 1;
  const toY = v => H - 28 - ((v - minP) / range) * (H - 56);
  const cw = (W - 40) / candles.length;
  const bw = cw * 0.6;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%' }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="chartBg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a1628" />
          <stop offset="100%" stopColor="#060e1e" />
        </linearGradient>
      </defs>
      <rect width={W} height={H} fill="url(#chartBg)" rx="4" />
      {[0.25, 0.5, 0.75].map(f => (
        <line key={f} x1={40} y1={H - 28 - f * (H - 56)} x2={W} y2={H - 28 - f * (H - 56)} stroke="#1e2d45" strokeWidth="1" strokeDasharray="4 4" />
      ))}
      {[0.25, 0.5, 0.75].map(f => {
        const val = minP + f * range;
        return <text key={f} x={36} y={H - 24 - f * (H - 56)} fill="#4a5568" fontSize="9" textAnchor="end" fontFamily="DM Mono, monospace">₹{val.toFixed(0)}</text>;
      })}
      {candles.map((c, i) => {
        const x = 42 + i * cw;
        const isGreen = c.close >= c.open;
        const color = isGreen ? '#22c55e' : '#ef4444';
        const topBody = toY(Math.max(c.open, c.close));
        const bodyH = Math.max(Math.abs(toY(c.open) - toY(c.close)), 1);
        const volH = c.vol * 22;
        return (
          <g key={i}>
            <line x1={x + bw / 2} y1={toY(c.high)} x2={x + bw / 2} y2={toY(c.low)} stroke={color} strokeWidth="1" opacity="0.8" />
            <rect x={x} y={topBody} width={bw} height={bodyH} fill={color} opacity={isGreen ? 0.85 : 0.9} rx="0.5" />
            <rect x={x} y={H - 16 - volH} width={bw} height={volH} fill={color} opacity="0.3" rx="0.5" />
          </g>
        );
      })}
    </svg>
  );
};

/* ─────────────────────────────────────────────
   SCORE METER
───────────────────────────────────────────── */
const ScoreMeter = ({ label, value, color, isText }) => (
  <div style={{ flex: 1, background: '#0a1628', border: '1px solid #1e2d45', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <div style={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    <div style={{ fontSize: isText ? '1rem' : '1.4rem', fontWeight: '700', color: color, fontFamily: 'DM Mono, monospace', lineHeight: 1 }}>{value}</div>
    {!isText && <div style={{ height: '3px', background: '#1e2d45', borderRadius: '2px', marginTop: '4px' }}>
      <div style={{ height: '100%', width: `${Math.min(value, 100)}%`, background: color, borderRadius: '2px', transition: 'width 0.6s ease' }} />
    </div>}
  </div>
);

/* ─────────────────────────────────────────────
   PARSE METRICS FROM FINANCIALS TEXT
───────────────────────────────────────────── */
const parseMetrics = (financialsText) => {
  const text = financialsText || '';
  const peMatch = text.match(/PE:\s*([\d.Nx]+)/i);
  const fcfMatch = text.match(/Free Cash Flow:\s*([+\-₹\d,CrNA]+)/i);
  const debtMatch = text.match(/Debt:\s*([\d.x]+)/i);
  return {
    pe: peMatch ? peMatch[1].replace('x', '') + '×' : 'N/A',
    fcf: fcfMatch ? fcfMatch[1] : 'N/A',
    debt: debtMatch ? debtMatch[1] + '×' : 'N/A',
  };
};

const parseScores = (company) => {
  const fin = company.tabs.financials || '';
  const tech = company.tabs.technicals || '';
  const peMatch = fin.match(/PE:\s*([\d.]+)/i);
  const pe = peMatch ? parseFloat(peMatch[1]) : 50;
  const debtMatch = fin.match(/Debt:\s*([\d.]+)/i);
  const debt = debtMatch ? parseFloat(debtMatch[1]) : 1;
  const fundamental = Math.max(20, Math.min(99, Math.round(100 - pe * 0.6 - debt * 8)));
  const rsiMatch = tech.match(/RSI:\s*([\d.]+)/i);
  const rsi = rsiMatch ? parseFloat(rsiMatch[1]) : 50;
  const technical = Math.round(rsi);
  const risk = debt > 2 ? 'HIGH' : debt > 1 ? 'MEDIUM' : 'LOW';
  const riskColor = debt > 2 ? '#ef4444' : debt > 1 ? '#f59e0b' : '#22c55e';
  const hasPositiveFCF = fin.includes('+₹') || fin.includes('+Rs');
  const conviction = hasPositiveFCF && debt < 0.5 ? 'HIGH' : debt > 2 ? 'LOW' : 'MEDIUM';
  const convColor = conviction === 'HIGH' ? '#22c55e' : conviction === 'LOW' ? '#ef4444' : '#f59e0b';
  return { fundamental, technical, risk, riskColor, conviction, convColor };
};

/* ─────────────────────────────────────────────
   WATCHLIST ROW
───────────────────────────────────────────── */
const WatchlistRow = React.memo(({ comp, isActive, isHeld, onClick, priceChangePct }) => {
  const isPos = priceChangePct >= 0;
  return (
    <div onClick={onClick} style={{
      padding: '10px 14px', cursor: 'pointer', background: isActive ? 'rgba(59,130,246,0.12)' : 'transparent',
      borderLeft: isActive ? '2px solid #3b82f6' : '2px solid transparent',
      borderBottom: '1px solid #0f1c2e', transition: 'background 0.15s', display: 'flex', flexDirection: 'column', gap: '3px'
    }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#f8fafc', fontFamily: 'DM Mono, monospace', letterSpacing: '-0.01em' }}>{comp.ticker}</span>
          {isHeld && <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />}
        </div>
        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: isPos ? '#22c55e' : '#ef4444', fontFamily: 'DM Mono, monospace' }}>
          {isPos ? '+' : ''}{priceChangePct.toFixed(1)}%
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.68rem', color: '#64748b' }}>₹{comp.currentPrice.toFixed(0)} · {comp.sector}</span>
      </div>
    </div>
  );
});

/* ─────────────────────────────────────────────
   ACCORDION SECTION
───────────────────────────────────────────── */
const AccordionSection = ({ label, tabKey, content, isViewed, isOpen, onToggle }) => {
  const ref = useRef(null);
  return (
    <div style={{ borderBottom: '1px solid #0f1c2e' }}>
      <div onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '11px 16px', cursor: 'pointer', userSelect: 'none',
        background: isOpen ? 'rgba(59,130,246,0.06)' : 'transparent', transition: 'background 0.15s'
      }}
        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.7rem', color: isViewed ? '#22c55e' : '#64748b', fontFamily: 'DM Mono, monospace', width: '10px' }}>{isViewed ? '✓' : '○'}</span>
          <span style={{ fontSize: '0.82rem', color: isViewed ? '#22c55e' : '#94a3b8', fontWeight: isViewed ? '500' : '400' }}>{label}</span>
        </div>
        <span style={{ color: '#64748b', fontSize: '0.65rem', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'inline-block' }}>▶</span>
      </div>
      <div style={{ overflow: 'hidden', maxHeight: isOpen ? '300px' : '0', transition: 'max-height 0.3s ease', background: '#060e1e' }}>
        <div ref={ref} style={{ padding: '14px 16px', fontSize: '0.8rem', color: '#cbd5e1', lineHeight: '1.75', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <DecryptText text={content} />
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   HEATMAP PANEL
───────────────────────────────────────────── */
const HeatmapPanel = ({ marketData, onSelectCompany }) => {
  const sectors = useMemo(() => {
    const s = {};
    marketData.forEach(c => { if (!s[c.sector]) s[c.sector] = []; s[c.sector].push(c); });
    return s;
  }, [marketData]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px', overflowY: 'auto', height: '100%' }}>
      {Object.entries(sectors).map(([sector, comps]) => (
        <div key={sector}>
          <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{sector}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {comps.map(c => {
              const pct = ((c.currentPrice - COMPANIES_V2.find(x => x.id === c.id).currentPrice) / COMPANIES_V2.find(x => x.id === c.id).currentPrice) * 100;
              const intense = Math.min(Math.abs(pct) / 15, 1);
              const bg = pct >= 0 ? `rgba(34,197,94,${0.15 + intense * 0.5})` : `rgba(239,68,68,${0.15 + intense * 0.5})`;
              return (
                <div key={c.id} onClick={() => onSelectCompany(c)} title={c.name} style={{
                  background: bg, border: `1px solid ${pct >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                  padding: '8px 10px', cursor: 'pointer', minWidth: '72px', borderRadius: '3px', transition: 'transform 0.15s'
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#f8fafc', fontFamily: 'DM Mono, monospace' }}>{c.ticker}</div>
                  <div style={{ fontSize: '0.65rem', color: pct >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'DM Mono, monospace', marginTop: '2px' }}>
                    {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   BOOT SCREEN
───────────────────────────────────────────── */
const BootScreen = ({ onComplete }) => (
  <div style={{ height: '100vh', background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '24px' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '1.8rem', fontWeight: '800', color: '#f8fafc', fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.03em', marginBottom: '6px' }}>
        VeeraStockIQ
      </div>
      <div style={{ fontSize: '0.75rem', color: '#3b82f6', fontFamily: 'DM Mono, monospace', letterSpacing: '0.15em' }}>PROFESSIONAL RESEARCH PLATFORM</div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
      <span style={{ color: '#64748b', fontSize: '0.78rem', fontFamily: 'DM Mono, monospace' }}>Connecting to market feeds</span>
    </div>
    <style>{`@keyframes pulse { 0%,100%{opacity:.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }`}</style>
    <AutoAdvance delay={1800} onComplete={onComplete} />
  </div>
);

const AutoAdvance = ({ delay, onComplete }) => {
  useEffect(() => { const t = setTimeout(onComplete, delay); return () => clearTimeout(t); }, [delay, onComplete]);
  return null;
};

/* ─────────────────────────────────────────────
   BRIEFING SCREEN
───────────────────────────────────────────── */
const BriefingScreen = ({ onEnter, currentSeason }) => (
  <div style={{ minHeight: '100vh', background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: '520px', background: '#060e1e', border: '1px solid #1e2d45', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
      <div style={{ background: '#0a1628', borderBottom: '1px solid #1e2d45', padding: '24px 28px' }}>
        <div style={{ fontSize: '0.65rem', color: '#3b82f6', fontFamily: 'DM Mono, monospace', letterSpacing: '0.12em', marginBottom: '8px' }}>VEERA CAPITAL · RESEARCH DIVISION</div>
        <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#f8fafc', fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em' }}>Good morning, Analyst VQ-1028.</div>
      </div>
      <div style={{ padding: '28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          {[['Session Date', '12 Jan 2028'], ['Market Trend', 'Bullish'], ['FGI Index', '78 — Greedy'], ['Capital Allotted', '₹10,00,000'], ['Benchmark', 'APEX 50'], ['Seasons', '5 Total']].map(([k, v]) => (
            <div key={k} style={{ background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '4px', padding: '10px 12px' }}>
              <div style={{ fontSize: '0.62rem', color: '#64748b', fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', marginBottom: '4px' }}>{k}</div>
              <div style={{ fontSize: '0.82rem', color: '#f8fafc', fontWeight: '600' }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '4px', padding: '14px 16px', marginBottom: '24px' }}>
          <div style={{ fontSize: '0.7rem', color: '#3b82f6', fontFamily: 'DM Mono, monospace', marginBottom: '10px', fontWeight: '600' }}>SESSION OBJECTIVES</div>
          {['Beat the APEX 50 benchmark across all 5 seasons', 'Preserve capital against macro shocks', 'Avoid high-debt value traps', 'Identify at least one long-term compounder'].map(o => (
            <div key={o} style={{ display: 'flex', gap: '8px', fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.6' }}>
              <span style={{ color: '#3b82f6', flexShrink: 0 }}>→</span>{o}
            </div>
          ))}
        </div>
        <button onClick={onEnter} style={{
          width: '100%', padding: '14px', background: '#3b82f6', border: 'none', borderRadius: '5px',
          color: '#fff', fontWeight: '700', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.9rem',
          cursor: 'pointer', transition: 'background 0.2s', letterSpacing: '-0.01em'
        }}
          onMouseEnter={e => e.target.style.background = '#2563eb'}
          onMouseLeave={e => e.target.style.background = '#3b82f6'}
        >
          Enter Platform →
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   RESULTS SCREEN
───────────────────────────────────────────── */
const ResultsScreen = ({ totalNetWorth, benchmarkReturnPct, netReturnPct, alpha, journalEntries, onComplete }) => (
  <div style={{ minHeight: '100vh', background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: '560px', background: '#060e1e', border: '1px solid #1e2d45', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
      <div style={{ background: '#0a1628', borderBottom: '1px solid #1e2d45', padding: '24px 28px', textAlign: 'center' }}>
        <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', letterSpacing: '0.1em', marginBottom: '8px' }}>SESSION COMPLETE</div>
        <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#f8fafc', fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.03em' }}>Final Performance Report</div>
      </div>
      <div style={{ padding: '28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          {[
            ['Net Worth', `₹${totalNetWorth.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, '#f8fafc'],
            ['Your Return', `${netReturnPct >= 0 ? '+' : ''}${netReturnPct}%`, netReturnPct >= 0 ? '#22c55e' : '#ef4444'],
            ['APEX 50 Return', `${benchmarkReturnPct >= 0 ? '+' : ''}${benchmarkReturnPct}%`, '#64748b'],
            ['Alpha Generated', `${alpha >= 0 ? '+' : ''}${alpha}%`, alpha >= 0 ? '#22c55e' : '#ef4444'],
          ].map(([k, v, c]) => (
            <div key={k} style={{ background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '4px', padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '0.62rem', color: '#64748b', fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', marginBottom: '6px' }}>{k}</div>
              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: c, fontFamily: 'DM Mono, monospace' }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '4px', padding: '14px 16px', marginBottom: '20px' }}>
          <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '10px' }}>JOURNAL ENTRIES FILED</div>
          {Object.keys(journalEntries).length === 0 ? (
            <div style={{ color: '#64748b', fontSize: '0.78rem' }}>No journal entries recorded.</div>
          ) : Object.entries(journalEntries).map(([id, j]) => (
            <div key={id} style={{ display: 'flex', gap: '8px', fontSize: '0.75rem', color: '#94a3b8', lineHeight: '1.7' }}>
              <span style={{ color: '#3b82f6', fontFamily: 'DM Mono, monospace' }}>{id.toUpperCase()}</span>
              <span>{j.tags?.join(', ') || 'No tags'}</span>
            </div>
          ))}
        </div>
        <button onClick={onComplete} style={{
          width: '100%', padding: '14px', background: 'transparent', border: '1px solid #1e2d45', borderRadius: '5px',
          color: '#94a3b8', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '0.85rem', cursor: 'pointer',
          transition: 'all 0.2s'
        }}
          onMouseEnter={e => { e.target.style.background = '#1e2d45'; e.target.style.color = '#f8fafc'; }}
          onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#94a3b8'; }}
        >
          Play Again
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   JOURNAL DRAWER
───────────────────────────────────────────── */
const JournalDrawer = ({ isOpen, company, pendingTrade, journalEntries, setJournalEntries, onCancel, onExecute }) => {
  const existing = journalEntries[company?.id] || { tags: [], notes: '' };
  const tags = ['Strong Fundamentals', 'Value Investment', 'High Dividend', 'Momentum', 'Swing Trade', 'News Based'];
  if (!company) return null;
  return (
    <>
      <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.5)', zIndex: 1000, opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'all' : 'none', transition: 'opacity 0.25s' }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', background: '#060e1e',
        borderLeft: '1px solid #1e2d45', zIndex: 1001, display: 'flex', flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)',
        boxShadow: '-16px 0 40px rgba(0,0,0,0.4)'
      }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #1e2d45', background: '#0a1628' }}>
          <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>INVESTMENT JOURNAL</div>
          <div style={{ fontSize: '1rem', fontWeight: '700', color: '#f8fafc' }}>
            {pendingTrade?.action} · {company.ticker} · {pendingTrade?.pct}% Allocation
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          <p style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: '1.6', marginBottom: '16px' }}>
            Professional analysts document their thesis before executing. Why are you allocating capital to {company.name}?
          </p>
          <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '10px', textTransform: 'uppercase' }}>Investment Rationale</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
            {tags.map(tag => {
              const sel = existing.tags.includes(tag);
              return (
                <button key={tag} onClick={() => setJournalEntries(prev => {
                  const ex = prev[company.id] || { tags: [], notes: '' };
                  const newTags = sel ? ex.tags.filter(t => t !== tag) : [...ex.tags, tag];
                  return { ...prev, [company.id]: { ...ex, tags: newTags } };
                })} style={{
                  padding: '6px 12px', fontSize: '0.73rem', border: `1px solid ${sel ? '#3b82f6' : '#1e2d45'}`,
                  background: sel ? 'rgba(59,130,246,0.15)' : 'transparent', color: sel ? '#3b82f6' : '#94a3b8',
                  cursor: 'pointer', borderRadius: '4px', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s'
                }}>{tag}</button>
              );
            })}
          </div>
          <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '8px', textTransform: 'uppercase' }}>Analyst Notes</div>
          <textarea
            placeholder="Document your investment thesis here..."
            value={existing.notes}
            onChange={e => setJournalEntries(prev => {
              const ex = prev[company.id] || { tags: [], notes: '' };
              return { ...prev, [company.id]: { ...ex, notes: e.target.value } };
            })}
            style={{
              width: '100%', height: '110px', background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '4px',
              color: '#f8fafc', padding: '12px', fontSize: '0.8rem', fontFamily: 'Plus Jakarta Sans, sans-serif',
              outline: 'none', resize: 'vertical', lineHeight: '1.6', boxSizing: 'border-box'
            }}
          />
          <div style={{ marginTop: '16px', padding: '12px 14px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: '4px', fontSize: '0.73rem', color: '#94a3b8', lineHeight: '1.6' }}>
            <span style={{ color: '#3b82f6' }}>Capital to deploy: </span>
            ₹{((pendingTrade?.pct || 0) * 10000).toLocaleString('en-IN')} · {pendingTrade?.pct}% of base
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #1e2d45', display: 'flex', gap: '10px' }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid #1e2d45', color: '#94a3b8', cursor: 'pointer', borderRadius: '4px', fontSize: '0.82rem', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.target.style.borderColor = '#64748b'; e.target.style.color = '#f8fafc'; }}
            onMouseLeave={e => { e.target.style.borderColor = '#1e2d45'; e.target.style.color = '#94a3b8'; }}
          >Cancel</button>
          <button onClick={onExecute} style={{ flex: 2, padding: '12px', background: '#22c55e', border: 'none', color: '#000', cursor: 'pointer', borderRadius: '4px', fontSize: '0.82rem', fontWeight: '700', transition: 'background 0.15s' }}
            onMouseEnter={e => e.target.style.background = '#16a34a'}
            onMouseLeave={e => e.target.style.background = '#22c55e'}
          >Execute and Log →</button>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   EMOTION CHECK CARD
───────────────────────────────────────────── */
const EmotionCheckCard = ({ data, onSelect }) => {
  if (!data) return null;
  const isGain = data.type === 'gain';
  return (
    <div style={{ position: 'fixed', bottom: '70px', right: '20px', width: '320px', background: '#060e1e', border: `1px solid ${isGain ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: '8px', zIndex: 1100, boxShadow: '0 16px 40px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', background: isGain ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', borderBottom: '1px solid #1e2d45' }}>
        <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>PORTFOLIO SWING DETECTED</div>
        <div style={{ fontSize: '1rem', fontWeight: '700', color: isGain ? '#22c55e' : '#ef4444', fontFamily: 'DM Mono, monospace' }}>
          {isGain ? '+' : ''}{data.pnlPct}% · {isGain ? 'Gain' : 'Loss'}
        </div>
      </div>
      <div style={{ padding: '14px 16px' }}>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: '1.5', marginBottom: '12px' }}>Before reviewing market data, what is your primary emotional response?</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
          {['Excited', 'Confident', 'Fear', 'Panic', 'Regret', 'Neutral'].map(emo => (
            <button key={emo} onClick={() => onSelect(emo)} style={{
              padding: '8px', background: '#0a1628', border: '1px solid #1e2d45', color: '#94a3b8',
              cursor: 'pointer', borderRadius: '4px', fontSize: '0.75rem', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s'
            }}
              onMouseEnter={e => { e.target.style.background = '#1e2d45'; e.target.style.color = '#f8fafc'; }}
              onMouseLeave={e => { e.target.style.background = '#0a1628'; e.target.style.color = '#94a3b8'; }}
            >{emo}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MORNING BRIEF PANEL
───────────────────────────────────────────── */
const MorningBriefPanel = ({ isOpen, currentSeason, benchmarkReturnPct, aiMentorQueue, onDismiss }) => (
  <>
    <div onClick={onDismiss} style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.6)', zIndex: 1000, opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'all' : 'none', transition: 'opacity 0.25s' }} />
    <div style={{
      position: 'fixed', top: 0, left: '50%', transform: `translateX(-50%) translateY(${isOpen ? '0' : '-100%'})`,
      width: '520px', background: '#060e1e', border: '1px solid #1e2d45', borderTop: 'none', zIndex: 1001,
      transition: 'transform 0.35s cubic-bezier(0.16,1,0.3,1)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', overflow: 'hidden'
    }}>
      <div style={{ background: '#0a1628', borderBottom: '1px solid #1e2d45', padding: '20px 24px' }}>
        <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>MARKET BRIEF · {currentSeason?.time} IST</div>
        <div style={{ fontSize: '1rem', fontWeight: '700', color: '#f8fafc' }}>{currentSeason?.name}</div>
      </div>
      <div style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1, background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '4px', padding: '10px 12px' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>APEX 50</div>
            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: parseFloat(benchmarkReturnPct) >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'DM Mono, monospace' }}>
              {parseFloat(benchmarkReturnPct) >= 0 ? '+' : ''}{benchmarkReturnPct}%
            </div>
          </div>
          <div style={{ flex: 2, background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '4px', padding: '10px 12px' }}>
            <div style={{ fontSize: '0.6rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>SEASON OVERVIEW</div>
            <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.5' }}>{currentSeason?.description}</div>
          </div>
        </div>
        {aiMentorQueue.length > 0 && (
          <div style={{ borderTop: '1px solid #1e2d45', paddingTop: '16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '0.65rem', color: '#f59e0b', fontFamily: 'DM Mono, monospace', marginBottom: '10px' }}>AI MENTOR · CONSEQUENCE DEBRIEF</div>
            {aiMentorQueue.map((msg, idx) => (
              <div key={idx} style={{ padding: '10px 12px', borderLeft: `2px solid ${msg.type === 'negative' ? '#ef4444' : '#22c55e'}`, marginBottom: '8px', background: '#0a1628' }}>
                <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>{msg.company}</div>
                <div style={{ fontSize: '0.75rem', color: '#cbd5e1', lineHeight: '1.6' }}>{msg.message}</div>
              </div>
            ))}
          </div>
        )}
        <button onClick={onDismiss} style={{
          width: '100%', padding: '12px', background: '#3b82f6', border: 'none', borderRadius: '4px',
          color: '#fff', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', transition: 'background 0.15s',
          fontFamily: 'Plus Jakarta Sans, sans-serif'
        }}
          onMouseEnter={e => e.target.style.background = '#2563eb'}
          onMouseLeave={e => e.target.style.background = '#3b82f6'}
        >
          Open Markets →
        </button>
      </div>
    </div>
  </>
);

/* ─────────────────────────────────────────────
   AI COPILOT BAR
───────────────────────────────────────────── */
const AICopilotBar = ({ message, isExpanded, onToggle, aiMentorQueue }) => (
  <div style={{ height: isExpanded ? 'auto' : '48px', background: '#060e1e', borderTop: '1px solid #1e2d45', flexShrink: 0, overflow: 'hidden', transition: 'height 0.3s ease' }}>
    <div onClick={onToggle} style={{ height: '48px', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '10px', cursor: 'pointer' }}>
      <span style={{ color: '#3b82f6', fontSize: '0.8rem', flexShrink: 0, fontWeight: '700' }}>✦ AI Copilot</span>
      <div style={{ flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{message}</span>
      </div>
      <button style={{
        padding: '5px 12px', background: '#1e2d45', border: '1px solid #2d3f57', borderRadius: '4px',
        color: '#94a3b8', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'DM Mono, monospace', flexShrink: 0, whiteSpace: 'nowrap'
      }}>Ask anything ↗</button>
    </div>
    {isExpanded && (
      <div style={{ padding: '0 16px 16px', borderTop: '1px solid #1e2d45' }}>
        <div style={{ maxHeight: '200px', overflowY: 'auto', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {aiMentorQueue.length > 0 ? aiMentorQueue.map((msg, i) => (
            <div key={i} style={{ padding: '10px 12px', borderLeft: `2px solid ${msg.type === 'negative' ? '#ef4444' : '#22c55e'}`, background: '#0a1628', fontSize: '0.75rem', color: '#cbd5e1', lineHeight: '1.6' }}>
              {msg.message}
            </div>
          )) : (
            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{message}</div>
          )}
        </div>
      </div>
    )}
  </div>
);

/* ─────────────────────────────────────────────
   MAIN ENGINE
───────────────────────────────────────────── */
export default function SimulationEngineV3_Prod({ onComplete }) {
  const [stage, setStage] = useState('BOOT');
  const [workspace, setWorkspace] = useState('RESEARCH');
  const [round, setRound] = useState(0);
  const [cash, setCash] = useState(1000000);
  const [portfolio, setPortfolio] = useState({});
  const [apex50Value, setApex50Value] = useState(10000);
  const [investigatedNodes, setInvestigatedNodes] = useState({});
  const [journalEntries, setJournalEntries] = useState({});
  const [showJournal, setShowJournal] = useState(false);
  const [pendingTrade, setPendingTrade] = useState(null);
  const [showEmotionCheck, setShowEmotionCheck] = useState(null);
  const [showMorningBrief, setShowMorningBrief] = useState(false);
  const [aiMentorQueue, setAiMentorQueue] = useState([]);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [marketData, setMarketData] = useState(COMPANIES_V2);
  const [eventLog, setEventLog] = useState(['SYSTEM ONLINE. AWAITING MARKET OPEN.']);
  const [activeCompany, setActiveCompany] = useState(COMPANIES_V2[0]);
  const [activeTab, setActiveTab] = useState('overview');
  const [openAccordion, setOpenAccordion] = useState('overview');
  const [aiBarExpanded, setAiBarExpanded] = useState(false);
  const [timeframe, setTimeframe] = useState('1D');

  const currentSeason = MARKET_SEASONS[round];
  const BASE_CAPITAL = 1000000;
  const TAB_KEYS = ['overview', 'financials', 'technicals', 'management', 'auditorNotes'];
  const TAB_LABELS = { overview: 'Overview', financials: 'Financials', technicals: 'Technicals', management: 'Management', auditorNotes: 'Auditor Notes' };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); if (stage === 'HUB') setShowCommandPalette(p => !p); }
      if (e.key === 'Escape') { setShowCommandPalette(false); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [stage]);

  const totalHoldingsValue = useMemo(() => Object.entries(portfolio).reduce((acc, [id, data]) => {
    const comp = marketData.find(c => c.id === id);
    return acc + (data.shares * (comp?.currentPrice || 0));
  }, 0), [portfolio, marketData]);

  const totalNetWorth = cash + totalHoldingsValue;
  const netReturnPct = (((totalNetWorth - BASE_CAPITAL) / BASE_CAPITAL) * 100).toFixed(2);
  const benchmarkReturnPct = (((apex50Value - 10000) / 10000) * 100).toFixed(2);
  const alpha = (parseFloat(netReturnPct) - parseFloat(benchmarkReturnPct)).toFixed(2);

  const handleTabClick = useCallback((tab) => {
    setActiveTab(tab);
    setOpenAccordion(tab);
    setInvestigatedNodes(prev => {
      const compNodes = prev[activeCompany.id] || [];
      if (!compNodes.includes(tab)) return { ...prev, [activeCompany.id]: [...compNodes, tab] };
      return prev;
    });
  }, [activeCompany.id]);

  const getClearancePct = (compId) => ((investigatedNodes[compId]?.length || 0) / TAB_KEYS.length) * 100;
  const isCleared = getClearancePct(activeCompany.id) === 100;
  const nodesCleared = investigatedNodes[activeCompany.id]?.length || 0;

  const triggerTradeFlow = (action, pct) => {
    setShowCommandPalette(false);
    if (action === 'BUY') { setPendingTrade({ action, pct }); setShowJournal(true); }
    else { executeTrade('SELL', activeCompany.id); }
  };

  const executeTrade = (action, payload, journalData = null) => {
    const comp = activeCompany;
    if (action === 'BUY') {
      const allocateAmount = BASE_CAPITAL * (payload / 100);
      if (cash < allocateAmount) return alert('Insufficient Liquid Reserve.');
      const sharesToBuy = Math.floor(allocateAmount / comp.currentPrice);
      const totalCost = sharesToBuy * comp.currentPrice;
      setCash(prev => prev - totalCost);
      setPortfolio(prev => {
        const existing = prev[comp.id] || { shares: 0, totalInvested: 0 };
        return { ...prev, [comp.id]: { shares: existing.shares + sharesToBuy, avgPrice: (existing.totalInvested + totalCost) / (existing.shares + sharesToBuy), totalInvested: existing.totalInvested + totalCost } };
      });
      if (journalData) setJournalEntries(prev => ({ ...prev, [comp.id]: journalData }));
    } else if (action === 'SELL') {
      const compId = payload;
      const existing = portfolio[compId];
      const actualComp = marketData.find(c => c.id === compId);
      if (!existing || existing.shares === 0) return;
      setCash(prev => prev + (existing.shares * actualComp.currentPrice));
      setPortfolio(prev => { const n = { ...prev }; delete n[compId]; return n; });
    }
  };

  const advanceTimeline = () => {
    if (round >= MARKET_SEASONS.length - 1) return setStage('FINISH');
    const nextRound = round + 1;
    const nextSeason = MARKET_SEASONS[nextRound];
    let newMentorMessages = [];
    let newEvents = [];
    const oldNetWorth = totalNetWorth;
    setApex50Value(prev => prev * (1 + (nextSeason.benchmarkReturn / 100)));
    const updatedMarket = marketData.map(comp => {
      let priceModifier = nextSeason.trendModifier;
      const triggeredEvents = EVENTS_POOL.filter(e => e.triggerSeason === nextRound && (e.targetId === comp.id || e.targetId === 'all'));
      triggeredEvents.forEach(event => {
        priceModifier *= event.impactMultiplier;
        newEvents.push(`[${nextSeason.time}] ${event.headline}`);
        if (portfolio[comp.id] && portfolio[comp.id].shares > 0 && event.aiTrigger) {
          const journal = journalEntries[comp.id];
          let customMsg = event.aiTrigger;
          if (journal && journal.tags.length > 0) customMsg = `Journal Memory: You invested based on [${journal.tags.join(', ')}]. Reality check: ` + customMsg;
          newMentorMessages.push({ company: comp.name, message: customMsg, type: event.impactMultiplier < 1 ? 'negative' : 'positive' });
        }
      });
      return { ...comp, currentPrice: parseFloat((comp.currentPrice * priceModifier).toFixed(2)) };
    });
    setMarketData(updatedMarket);
    setEventLog(prev => [...newEvents, ...prev].slice(0, 10));
    setRound(nextRound);
    const newHoldingsValue = Object.entries(portfolio).reduce((acc, [id, data]) => acc + (data.shares * updatedMarket.find(c => c.id === id).currentPrice), 0);
    const swingPct = (((cash + newHoldingsValue) - oldNetWorth) / oldNetWorth) * 100;
    if (Math.abs(swingPct) > 5) {
      setShowEmotionCheck({ type: swingPct > 0 ? 'gain' : 'loss', pnlPct: swingPct.toFixed(1) });
      if (newMentorMessages.length > 0) setAiMentorQueue(newMentorMessages);
    } else {
      if (newMentorMessages.length > 0) setAiMentorQueue(newMentorMessages);
      setShowMorningBrief(true);
    }
  };

  const scores = useMemo(() => parseScores(activeCompany), [activeCompany]);
  const metrics = useMemo(() => parseMetrics(activeCompany.tabs.financials), [activeCompany]);
  const isFCFPositive = (metrics.fcf || '').includes('+');
  const debtNum = parseFloat((metrics.debt || '0').replace('×', ''));
  const copilotMessage = isCleared
    ? `Investigation complete for ${activeCompany.ticker}. Conviction data compiled — order ticket unlocked.`
    : `Do not rush. Review Auditor Notes — paper profits don't always match cash realities.`;

  if (stage === 'BOOT') return <BootScreen onComplete={() => setStage('BRIEFING')} />;
  if (stage === 'BRIEFING') return <BriefingScreen currentSeason={currentSeason} onEnter={() => { setStage('HUB'); setShowMorningBrief(true); }} />;
  if (stage === 'FINISH') return <ResultsScreen totalNetWorth={totalNetWorth} benchmarkReturnPct={benchmarkReturnPct} netReturnPct={parseFloat(netReturnPct)} alpha={parseFloat(alpha)} journalEntries={journalEntries} onComplete={() => { setStage('BOOT'); setRound(0); setCash(1000000); setPortfolio({}); setApex50Value(10000); setInvestigatedNodes({}); setJournalEntries({}); setMarketData(COMPANIES_V2); setEventLog(['SYSTEM ONLINE. AWAITING MARKET OPEN.']); setActiveCompany(COMPANIES_V2[0]); }} />;

  return (
    <div style={{ height: '100vh', background: '#020617', display: 'flex', flexDirection: 'column', fontFamily: 'Plus Jakarta Sans, sans-serif', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1e2d45; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: #2d3f57; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        @media (max-width: 1024px) {
          .watchlist-panel { display: none !important; }
          .research-panel { width: 300px !important; }
        }
        @media (max-width: 768px) {
          .hub-grid { grid-template-columns: 1fr !important; }
          .research-panel { display: none !important; }
          .mobile-bottom-bar { display: flex !important; }
        }
      `}</style>

      {/* ── TOP NAV BAR ── */}
      <div style={{ height: '48px', background: '#060e1e', borderBottom: '1px solid #1e2d45', display: 'flex', alignItems: 'center', padding: '0 16px', gap: '16px', flexShrink: 0, zIndex: 10 }}>
        <div style={{ fontSize: '1rem', fontWeight: '800', color: '#f8fafc', letterSpacing: '-0.03em', flexShrink: 0 }}>VeeraStockIQ</div>
        <div style={{ width: '1px', height: '20px', background: '#1e2d45', flexShrink: 0 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, overflow: 'hidden' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#22c55e', flexShrink: 0, animation: 'shimmer 2s infinite' }} />
          <span style={{ fontSize: '0.73rem', color: '#64748b', fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {currentSeason.name.replace('Season ', '')} · APEX-50 <span style={{ color: parseFloat(benchmarkReturnPct) >= 0 ? '#22c55e' : '#ef4444' }}>{parseFloat(benchmarkReturnPct) >= 0 ? '+' : ''}{benchmarkReturnPct}%</span> · {currentSeason.time} IST · Season {round + 1} of {MARKET_SEASONS.length}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <div style={{ display: 'flex', background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '6px', overflow: 'hidden' }}>
            {['RESEARCH', 'WAR_ROOM'].map(w => (
              <button key={w} onClick={() => setWorkspace(w)} style={{
                padding: '5px 12px', background: workspace === w ? (w === 'RESEARCH' ? '#3b82f6' : '#f59e0b') : 'transparent',
                border: 'none', color: workspace === w ? '#fff' : '#64748b', cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: '600', fontFamily: 'Plus Jakarta Sans, sans-serif', transition: 'all 0.15s'
              }}>
                {w === 'RESEARCH' ? 'Research' : 'War Room'}
              </button>
            ))}
          </div>
          <button onClick={advanceTimeline} style={{
            padding: '5px 12px', background: 'transparent', border: '1px solid #1e2d45', borderRadius: '5px',
            color: '#94a3b8', cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'DM Mono, monospace', transition: 'all 0.15s', whiteSpace: 'nowrap'
          }}
            onMouseEnter={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.color = '#3b82f6'; }}
            onMouseLeave={e => { e.target.style.borderColor = '#1e2d45'; e.target.style.color = '#94a3b8'; }}
          >Next Season →</button>
          <div style={{ padding: '4px 10px', background: '#0a1628', border: '1px solid #1e2d45', borderRadius: '5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#fff', fontWeight: '700' }}>V</div>
            <span style={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'DM Mono, monospace' }}>VQ-1028 ▾</span>
          </div>
        </div>
      </div>

      {/* ── WORKSPACE ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {workspace === 'WAR_ROOM' ? (
          /* ── WAR ROOM ── */
          <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr 280px', gap: '0', flex: 1, overflow: 'hidden' }}>
            {/* Portfolio */}
            <div style={{ borderRight: '1px solid #1e2d45', background: '#060e1e', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e2d45', background: '#0a1628', flexShrink: 0 }}>
                <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '6px' }}>PORTFOLIO SUMMARY</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#f8fafc', fontFamily: 'DM Mono, monospace' }}>₹{totalNetWorth.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
                  {[['Return', `${parseFloat(netReturnPct) >= 0 ? '+' : ''}${netReturnPct}%`, parseFloat(netReturnPct) >= 0 ? '#22c55e' : '#ef4444'], ['Alpha', `${parseFloat(alpha) >= 0 ? '+' : ''}${alpha}%`, parseFloat(alpha) >= 0 ? '#22c55e' : '#ef4444']].map(([l, v, c]) => (
                    <div key={l}>
                      <div style={{ fontSize: '0.6rem', color: '#64748b', fontFamily: 'DM Mono, monospace' }}>{l}</div>
                      <div style={{ fontSize: '0.82rem', color: c, fontFamily: 'DM Mono, monospace', fontWeight: '600' }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                <div style={{ padding: '8px 16px', borderBottom: '1px solid #0f1c2e' }}>
                  <div style={{ fontSize: '0.6rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>CASH</div>
                  <div style={{ fontSize: '0.85rem', color: '#f8fafc', fontFamily: 'DM Mono, monospace' }}>₹{cash.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                </div>
                {Object.entries(portfolio).length === 0 ? (
                  <div style={{ padding: '20px 16px', fontSize: '0.75rem', color: '#64748b' }}>No positions held.</div>
                ) : Object.entries(portfolio).map(([id, pos]) => {
                  const comp = marketData.find(c => c.id === id);
                  const pnl = (comp.currentPrice - pos.avgPrice) * pos.shares;
                  const pnlPct = ((comp.currentPrice - pos.avgPrice) / pos.avgPrice) * 100;
                  return (
                    <div key={id} onClick={() => { setActiveCompany(comp); setWorkspace('RESEARCH'); }} style={{ padding: '10px 16px', borderBottom: '1px solid #0f1c2e', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#f8fafc', fontFamily: 'DM Mono, monospace' }}>{comp.ticker}</span>
                        <span style={{ fontSize: '0.75rem', color: pnl >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'DM Mono, monospace' }}>{pnl >= 0 ? '+' : ''}₹{Math.abs(pnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '0.67rem', color: '#64748b' }}>{pos.shares} shares · avg ₹{pos.avgPrice.toFixed(0)}</span>
                        <span style={{ fontSize: '0.67rem', color: pnlPct >= 0 ? '#22c55e' : '#ef4444', fontFamily: 'DM Mono, monospace' }}>{pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Heatmap */}
            <div style={{ background: '#020617', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e2d45', background: '#060e1e', flexShrink: 0 }}>
                <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace' }}>MARKET HEATMAP · Click to research</div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <HeatmapPanel marketData={marketData} onSelectCompany={(comp) => { setActiveCompany(comp); setWorkspace('RESEARCH'); }} />
              </div>
            </div>
            {/* News */}
            <div style={{ borderLeft: '1px solid #1e2d45', background: '#060e1e', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e2d45', background: '#0a1628', flexShrink: 0 }}>
                <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace' }}>LIVE MARKET INTELLIGENCE</div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                {eventLog.map((log, i) => (
                  <div key={i} style={{ padding: '10px 12px', borderLeft: `2px solid ${i === 0 ? '#3b82f6' : '#1e2d45'}`, marginBottom: '8px', background: '#0a1628', borderRadius: '0 4px 4px 0' }}>
                    <div style={{ fontSize: '0.67rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>MARKET FEED</div>
                    <div style={{ fontSize: '0.75rem', color: '#cbd5e1', lineHeight: '1.5' }}>{log}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ── RESEARCH WORKSPACE ── */
          <div className="hub-grid" style={{ display: 'grid', gridTemplateColumns: '220px 1fr 300px', flex: 1, overflow: 'hidden' }}>

            {/* ── LEFT: WATCHLIST ── */}
            <div className="watchlist-panel" style={{ borderRight: '1px solid #1e2d45', background: '#060e1e', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #1e2d45', background: '#0a1628', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '600', color: '#94a3b8' }}>Watchlist</span>
                <span style={{ fontSize: '0.7rem', color: '#64748b', cursor: 'pointer' }}>⊕</span>
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {marketData.map(comp => {
                  const orig = COMPANIES_V2.find(c => c.id === comp.id);
                  const pct = ((comp.currentPrice - orig.currentPrice) / orig.currentPrice) * 100;
                  return (
                    <WatchlistRow
                      key={comp.id}
                      comp={comp}
                      isActive={comp.id === activeCompany.id}
                      isHeld={!!portfolio[comp.id]}
                      onClick={() => { setActiveCompany(comp); setActiveTab('overview'); setOpenAccordion('overview'); }}
                      priceChangePct={pct}
                    />
                  );
                })}
              </div>
              <div style={{ padding: '10px 14px', borderTop: '1px solid #1e2d45', background: '#0a1628' }}>
                <div style={{ fontSize: '0.67rem', color: '#64748b', textAlign: 'center' }}>+ {Math.max(0, 15 - marketData.length)} more stocks</div>
              </div>
            </div>

            {/* ── CENTER: CHART PANEL ── */}
            <div style={{ background: '#020617', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #1e2d45' }}>
              {/* Company Header */}
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e2d45', background: '#060e1e', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                <div>
                  <span style={{ fontSize: '1rem', fontWeight: '700', color: '#f8fafc' }}>{activeCompany.name}</span>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', marginLeft: '8px', fontFamily: 'DM Mono, monospace' }}>{activeCompany.ticker} · {activeCompany.sector}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#22c55e', fontFamily: 'DM Mono, monospace' }}>₹{activeCompany.currentPrice.toLocaleString('en-IN')}</span>
                  <span style={{ fontSize: '0.73rem', color: '#22c55e' }}>▲ +0.9%</span>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <span style={{ fontSize: '0.7rem', color: nodesCleared === 5 ? '#22c55e' : '#f59e0b', background: nodesCleared === 5 ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)', border: `1px solid ${nodesCleared === 5 ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'}`, padding: '3px 8px', borderRadius: '4px', fontFamily: 'DM Mono, monospace' }}>
                    {nodesCleared} / 5 investigated
                  </span>
                </div>
              </div>

              {/* Timeframe Bar */}
              <div style={{ padding: '8px 16px', borderBottom: '1px solid #1e2d45', background: '#060e1e', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                {['1D', '1W', '1M', '1Y', '5Y'].map(tf => (
                  <button key={tf} onClick={() => setTimeframe(tf)} style={{
                    padding: '3px 10px', background: timeframe === tf ? '#3b82f6' : 'transparent',
                    border: 'none', color: timeframe === tf ? '#fff' : '#64748b',
                    cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'DM Mono, monospace', borderRadius: '4px', transition: 'all 0.15s'
                  }}>{tf}</button>
                ))}
                <div style={{ flex: 1 }} />
                {['MA20', 'MA50', 'RSI', 'MACD', 'Volume'].map(ind => (
                  <span key={ind} style={{ fontSize: '0.65rem', color: '#4a5568', fontFamily: 'DM Mono, monospace' }}>{ind}{ind !== 'Volume' ? ' ·' : ''}</span>
                ))}
              </div>

              {/* Chart */}
              <div style={{ flex: 1, padding: '12px 16px 0', overflow: 'hidden', minHeight: 0 }}>
                <CandlestickChart company={activeCompany} currentPrice={activeCompany.currentPrice} />
              </div>

              {/* Chart Footer */}
              <div style={{ padding: '8px 16px', borderTop: '1px solid #1e2d45', background: '#060e1e', flexShrink: 0 }}>
                <div style={{ fontSize: '0.68rem', color: '#64748b', fontFamily: 'DM Mono, monospace', textAlign: 'center' }}>
                  {activeCompany.tabs.technicals.split('|')[1]?.trim() || 'Steady 45° Ascending Channel'} · Low Volatility · RSI {activeCompany.tabs.technicals.match(/RSI:\s*([\d.]+)/)?.[1] || '55'}
                </div>
              </div>

              {/* Score Meters */}
              <div style={{ display: 'flex', gap: '1px', borderTop: '1px solid #1e2d45', flexShrink: 0 }}>
                <ScoreMeter label="Fundamental" value={scores.fundamental} color="#f8fafc" />
                <ScoreMeter label="Technical" value={scores.technical} color="#f8fafc" />
                <ScoreMeter label="Risk" value={scores.risk} color={scores.riskColor} isText />
                <ScoreMeter label="Conviction" value={scores.conviction} color={scores.convColor} isText />
              </div>

              {/* Quick Metrics */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1px', borderTop: '1px solid #1e2d45', flexShrink: 0 }}>
                {[
                  ['PE Ratio', metrics.pe, '#f8fafc'],
                  ['Free Cash Flow', metrics.fcf, isFCFPositive ? '#22c55e' : '#ef4444'],
                  ['Debt / Equity', metrics.debt, debtNum > 1.5 ? '#ef4444' : debtNum > 0.5 ? '#f59e0b' : '#22c55e'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ background: '#0a1628', padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.62rem', color: '#64748b', fontFamily: 'DM Mono, monospace', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color, fontFamily: 'DM Mono, monospace' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── RIGHT: RESEARCH PANEL ── */}
            <div className="research-panel" style={{ background: '#060e1e', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Investigation Header */}
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e2d45', background: '#0a1628', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#f8fafc' }}>Investigation</span>
                  <span style={{ fontSize: '0.75rem', fontWeight: '700', color: isCleared ? '#22c55e' : '#f59e0b', fontFamily: 'DM Mono, monospace' }}>{nodesCleared} / 5 nodes</span>
                </div>
                <div style={{ height: '3px', background: '#1e2d45', borderRadius: '2px' }}>
                  <div style={{ height: '100%', width: `${(nodesCleared / 5) * 100}%`, background: isCleared ? '#22c55e' : '#3b82f6', borderRadius: '2px', transition: 'width 0.4s ease' }} />
                </div>
              </div>

              {/* Accordion */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {TAB_KEYS.map(tab => (
                  <AccordionSection
                    key={tab}
                    label={TAB_LABELS[tab]}
                    tabKey={tab}
                    content={activeCompany.tabs[tab]}
                    isViewed={investigatedNodes[activeCompany.id]?.includes(tab)}
                    isOpen={openAccordion === tab}
                    onToggle={() => handleTabClick(tab)}
                  />
                ))}

                {/* Hidden Intel */}
                <div style={{ borderBottom: '1px solid #0f1c2e' }}>
                  <div style={{ padding: '11px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: isCleared ? 1 : 0.6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.82rem' }}>{isCleared ? '🔓' : '🔒'}</span>
                      <span style={{ fontSize: '0.82rem', color: isCleared ? '#f59e0b' : '#64748b', fontWeight: isCleared ? '600' : '400' }}>Hidden Intel</span>
                    </div>
                    {!isCleared && <span style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace' }}>5/5 req.</span>}
                  </div>
                  {isCleared && (
                    <div style={{ padding: '12px 16px', background: 'rgba(245,158,11,0.05)', borderLeft: '2px solid #f59e0b', margin: '0 0 0 0' }}>
                      <div style={{ fontSize: '0.75rem', color: '#fbbf24', lineHeight: '1.7' }}>
                        <DecryptText text={activeCompany.hiddenIntelligence} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Ticket */}
                <div style={{ padding: '14px 16px', borderTop: '1px solid #1e2d45', background: '#0a1628' }}>
                  {!isCleared && (
                    <div style={{ padding: '8px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '4px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '0.7rem' }}>⚠</span>
                      <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>Complete investigation to trade</span>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                    {[['BUY', '#22c55e', '#000'], ['SELL', '#ef4444', '#fff']].map(([action, bg, fg]) => (
                      <button key={action} onClick={() => isCleared ? (action === 'BUY' ? null : triggerTradeFlow('SELL', null)) : null}
                        disabled={!isCleared}
                        style={{
                          padding: '10px', background: isCleared ? bg : '#1e2d45', border: 'none', borderRadius: '4px',
                          color: isCleared ? fg : '#64748b', fontWeight: '800', fontSize: '0.82rem', cursor: isCleared ? 'pointer' : 'not-allowed',
                          fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '0.05em', transition: 'all 0.15s', opacity: isCleared ? 1 : 0.5
                        }}
                        onMouseEnter={e => { if (isCleared) e.target.style.filter = 'brightness(1.1)'; }}
                        onMouseLeave={e => { e.target.style.filter = 'brightness(1)'; }}
                      >
                        {action === 'BUY' ? (
                          <span onClick={() => isCleared && setShowCommandPalette(true)}>{action}</span>
                        ) : action}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                    {[5, 10, 20].map(pct => (
                      <button key={pct} onClick={() => isCleared && triggerTradeFlow('BUY', pct)} style={{
                        flex: 1, padding: '6px', background: 'transparent', border: '1px solid #1e2d45', borderRadius: '4px',
                        color: isCleared ? '#94a3b8' : '#4a5568', cursor: isCleared ? 'pointer' : 'not-allowed', fontSize: '0.7rem',
                        fontFamily: 'DM Mono, monospace', transition: 'all 0.15s'
                      }}
                        onMouseEnter={e => { if (isCleared) { e.target.style.borderColor = '#3b82f6'; e.target.style.color = '#3b82f6'; } }}
                        onMouseLeave={e => { e.target.style.borderColor = '#1e2d45'; e.target.style.color = isCleared ? '#94a3b8' : '#4a5568'; }}
                      >Allocate: {pct}%</button>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.67rem', color: '#64748b', fontFamily: 'DM Mono, monospace', lineHeight: '1.7' }}>
                    Available: ₹{cash.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </div>
                  <div style={{ fontSize: '0.67rem', color: '#64748b', fontStyle: 'italic', marginTop: '4px', lineHeight: '1.5' }}>
                    Journal entry required before execution
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── BOTTOM AI BAR ── */}
      <AICopilotBar message={copilotMessage} isExpanded={aiBarExpanded} onToggle={() => setAiBarExpanded(p => !p)} aiMentorQueue={aiMentorQueue} />

      {/* ── OVERLAYS ── */}
      <JournalDrawer
        isOpen={showJournal}
        company={activeCompany}
        pendingTrade={pendingTrade}
        journalEntries={journalEntries}
        setJournalEntries={setJournalEntries}
        onCancel={() => { setShowJournal(false); setPendingTrade(null); }}
        onExecute={() => { executeTrade('BUY', pendingTrade.pct, journalEntries[activeCompany.id]); setShowJournal(false); setPendingTrade(null); }}
      />

      <EmotionCheckCard data={showEmotionCheck} onSelect={() => { setShowEmotionCheck(null); setShowMorningBrief(true); }} />

      <MorningBriefPanel
        isOpen={showMorningBrief}
        currentSeason={currentSeason}
        benchmarkReturnPct={benchmarkReturnPct}
        aiMentorQueue={aiMentorQueue}
        onDismiss={() => { setShowMorningBrief(false); setAiMentorQueue([]); }}
      />

      {/* ── COMMAND PALETTE ── */}
      {showCommandPalette && (
        <div onClick={() => setShowCommandPalette(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.8)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14vh' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '560px', background: '#060e1e', border: '1px solid #3b82f6', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.6)' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #1e2d45', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ color: '#3b82f6' }}>❯</span>
              <input type="text" placeholder={`Execute command on ${activeCompany.ticker}...`} autoFocus readOnly style={{ flex: 1, background: 'transparent', border: 'none', color: '#f8fafc', fontSize: '0.9rem', fontFamily: 'DM Mono, monospace', outline: 'none' }} />
              <span style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'DM Mono, monospace', border: '1px solid #1e2d45', padding: '2px 6px', borderRadius: '3px' }}>ESC</span>
            </div>
            <div style={{ padding: '8px' }}>
              <div style={{ padding: '8px 12px', fontSize: '0.62rem', color: '#64748b', fontFamily: 'DM Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Available Actions</div>
              {!isCleared ? (
                <div style={{ padding: '14px 12px', color: '#f59e0b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>⚠</span> Investigation incomplete — trading locked
                </div>
              ) : (
                <>
                  {[5, 10, 20].map(pct => (
                    <div key={pct} onClick={() => { triggerTradeFlow('BUY', pct); setShowCommandPalette(false); }} style={{
                      padding: '12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      borderRadius: '4px', transition: 'background 0.1s', marginBottom: '2px'
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div>
                        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.82rem', color: '#f8fafc' }}>Allocate {pct}% Capital</span>
                        <span style={{ fontSize: '0.7rem', color: '#64748b', marginLeft: '8px' }}>→ BUY {activeCompany.ticker}</span>
                      </div>
                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.78rem', color: '#22c55e' }}>₹{(BASE_CAPITAL * pct / 100).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                  {portfolio[activeCompany.id]?.shares > 0 && (
                    <div onClick={() => { triggerTradeFlow('SELL', null); setShowCommandPalette(false); }} style={{
                      padding: '12px', cursor: 'pointer', borderRadius: '4px', transition: 'background 0.1s',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: '0.82rem', color: '#ef4444' }}>Liquidate Entire Position</span>
                      <span style={{ fontSize: '0.7rem', color: '#64748b' }}>SELL ALL</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}